import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CloseButton, Form, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft, faCopy } from '@fortawesome/free-regular-svg-icons';
import { faAdd, faCheck, faEdit, faFolder, faRedo, faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../template/store';
import { AESDecrypt, AESEncrypt, encryptAndEncode } from '../../../encrypt-util';
import { getDateFormat } from '../../../helpers/dateFormat';
import { useBackNavigation } from '../../../hooks/BackNavigation';
import useBlCountry from '../../../hooks/useBlCountry';
import UploadThing from '../../../components/common/UploadThing';
import {
  approveRejectDoExtension,
  getInstructionList,
  getDemurrage,
  getDoExtenstionDetails,
  getLastestExtensionRequest,
} from '../dashboard/actions/instruction';
import { updateDashbaordRefresh } from '../../common/components/DataTable/slices/DataTable';
import DataTable from '../../common/components/DataTable';
import { IDataTableColumn } from '../../common/types/dataTable';
import {
  AttachmentConfig,
  DropOffContainer,
  fetchDropOffAttachmentConfig,
  submitContainerDropOff,
} from './actions';
type DateValues = Record<string, Date | null>;

type DocumentRow = {
  key: string;
  docId: number | null;
  docName: string;
  mandatory: boolean;
  dndInstructionAttId?: number | null;
  fileName: string;
  filePath?: string | null;
  serverPath?: string | null;
  tempFileName?: string | null;
  loading: number;
};

const formatPayloadDate = (date: Date | null) => date ? moment(date).format('YYYY-MM-DD') : null;
const newKey = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const INSTRUCTION_TYPE = 824;

const ContainerDropOff = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const onBack = useBackNavigation();
  const { blNum, encryptdoc } = useParams();
  const encryptedBlId = blNum ?? '';
  const blId = AESDecrypt(blNum);
  const requestId = AESDecrypt(encryptdoc);
  const selectedLinerId = useSelector((state: RootState) => state.navbarTop.linerId);
  const profile = useSelector((state: RootState) => state.userProfile.profile);
  const approvalAccess = useSelector((state: RootState) => state.userProfile.rbacDetails.containerDropOffApproveReject);
  const oblNumber = useSelector((state: RootState) => state.oblDetails.oblForm.number);
  const documentHistory = useSelector((state: RootState) => state.documentHistory.data);
  const latestDropOffRequest = useMemo(() => {
const requests = documentHistory.filter(item => String(item.instructionId) === String(INSTRUCTION_TYPE));
    return requests.length
      ? requests.reduce((latest, item) =>
          Number(item.blEventId ?? 0) > Number(latest.blEventId ?? 0) ? item : latest
        )
      : null;
  }, [documentHistory]);
  const editableStatuses = ['Draft', 'Submitted', 'Rejected'];
  const effectiveRequestId = requestId || (
    latestDropOffRequest && editableStatuses.includes(latestDropOffRequest.event)
      ? latestDropOffRequest.docId
      : null
  );
  const linerId = profile?.partnerType === 'liner' ? profile?.partnerId : selectedLinerId;
  const [blCountry] = useBlCountry(
    effectiveRequestId ? 'view' : 'new',
    'import',
    INSTRUCTION_TYPE
  );


  const [containers, setContainers] = useState<DropOffContainer[]>([]);
  const [returnDates, setReturnDates] = useState<DateValues>({});
  const [returnDateForAll, setReturnDateForAll] = useState<Date | null>(null);
  const [attachmentConfig, setAttachmentConfig] = useState<AttachmentConfig[]>([]);
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [remarks, setRemarks] = useState('');
  const [linerRemarks, setLinerRemarks] = useState('');
  const [customerEmailId, setCustomerEmailId] = useState<string | null>(null);
  const [version, setVersion] = useState<number | null>(0);
  const [dndInstructionId, setDndInstructionId] = useState<number | null>(null);
  const [displayBlNumber, setDisplayBlNumber] = useState('');
  const [readOnly, setReadOnly] = useState(Boolean(effectiveRequestId));
  const [requestStatus, setRequestStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dndRequestExists, setDndRequestExists] = useState(true);
  const [showDndSoftStop, setShowDndSoftStop] = useState(false);

  const isLiner = profile?.partnerType === 'liner';
  const canApproveReject = isLiner && approvalAccess === 'Y' && readOnly && requestStatus === 'Submitted';
  const canEdit = !isLiner && readOnly && editableStatuses.includes(requestStatus);
  const hasActiveUpload = documents.some(doc => doc.loading > 0 && doc.loading < 100);

  const configuredDocumentOptions = useMemo(
    () => attachmentConfig
      .filter(item => item.isMandatory === 'H')
      .map(item => ({ value: item.docId, label: item.docName })),
    [attachmentConfig]
  );

  const buildConfiguredDocuments = (
    configs: AttachmentConfig[],
    existingAttachments: any[] = []
  ): DocumentRow[] => {
    const rows: DocumentRow[] = existingAttachments.map(item => {
      const documentConfig = configs.find(configItem => configItem.docId === item.docId);
      return {
        key: newKey(),
        docId: item.docId ?? null,
        docName: documentConfig?.docName ?? t('resource_1:supportingDocument'),
        mandatory: documentConfig?.isMandatory === 'Y',
        dndInstructionAttId: item.dndInstructionAttId ?? null,
        fileName: item.fileName ?? '',
        filePath: item.filePath ?? null,
        loading: 100,
      };
    });

    configs
      .filter(item => item.isMandatory === 'Y' && !rows.some(row => row.docId === item.docId))
      .forEach(item => rows.push({
        key: newKey(),
        docId: item.docId,
        docName: item.docName,
        mandatory: true,
        fileName: '',
        loading: 0,
      }));

    return rows;
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!blId || !linerId || !blCountry) return;
      setLoading(true);
      try {
        const [containerResponse, documentConfig, dndResponse, requestResponse] = await Promise.all([
          dispatch(getDemurrage({
            docId: blId,
            selectedLinerId: linerId,
            showPickupLocationFields: false,
          })).unwrap(),
          fetchDropOffAttachmentConfig(blCountry, linerId),
          dispatch(getLastestExtensionRequest({ blId, linerId })).unwrap(),
          effectiveRequestId ? dispatch(getDoExtenstionDetails({
            docId: effectiveRequestId,
            selectedLinerId: linerId,
            liner: linerId,
            country: blCountry,
            instructionType: INSTRUCTION_TYPE,
          })).unwrap() : Promise.resolve(null),
        ]);
        if (!active) return;

     
        const request: any = requestResponse?.data;
        const latestDnd = dndResponse as any;
        const dndAbsent = latestDnd?.data?.statusCode === 400 ||
          latestDnd?.response?.status === 400;
        if (!dndAbsent && !latestDnd?.data) throw latestDnd;
        if (effectiveRequestId && !request?.dndInstructionId) {
          throw new Error("Unable to load Container Drop Off");
        }
        const requestContainers = request?.list?.length
          ? request.list
          : containerResponse.list;
        const nextReturnDates: DateValues = {};
        requestContainers.forEach((item: any) => {
          nextReturnDates[item.containerNo] = item.returnDate ? new Date(item.returnDate) : null;
        });
        const nextDocuments = buildConfiguredDocuments(
          documentConfig,
          request?.extensionAttList?.filter((item: any) => item.fileName) ?? []
        );
        setContainers(requestContainers);
        setReturnDates(nextReturnDates);
        setAttachmentConfig(documentConfig);
        setDocuments(nextDocuments);
        setRemarks(request?.userRemarks ?? '');
        setLinerRemarks(request?.remarks ?? '');
        setCustomerEmailId(request?.customerEmailId ?? null);
        setReadOnly(Boolean(effectiveRequestId));
        setRequestStatus(request?.status ?? '');
        setVersion(request?.version ?? 0);
        setDndInstructionId(request?.dndInstructionId ?? null);
        setDisplayBlNumber(request?.blNo ?? oblNumber ?? '');
        setDndRequestExists(!dndAbsent);
      } catch (error: any) {
        toast.error(error?.response?.data?.responseMsg ?? "Unable to load Container Drop Off");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [blId, linerId, blCountry, effectiveRequestId]);

  const updateDocument = (key: string, changes: Partial<DocumentRow>) => {
    setDocuments(rows => rows.map(row => row.key === key ? { ...row, ...changes } : row));
  };

  const addDocument = () => {
    if (documents.length >= 10 || !configuredDocumentOptions.length) return;
    const option = configuredDocumentOptions[0];
    setDocuments(rows => [...rows, {
      key: newKey(),
      docId: option.value,
      docName: option.label,
      mandatory: false,
      fileName: '',
      loading: 0,
    }]);
  };

  const removeDocument = (row: DocumentRow) => {
    if (row.mandatory) {
      updateDocument(row.key, {
        fileName: '',
        filePath: null,
        serverPath: null,
        tempFileName: null,
        loading: 0,
        dndInstructionAttId: null,
      });
      return;
    }
    setDocuments(rows => rows.filter(item => item.key !== row.key));
  };

  const validate = () => {
    if (!containers.length) {
      toast.error("No containers are available for this BL");
      return false;
    }
    if (containers.some(container => !returnDates[container.containerNo])) {
      toast.error(`${t('resource_1:intendedReturnDate')} is required for every container`);
      return false;
    }
    if (!documents.some(document => document.fileName)) {
      toast.error("At least one supporting document is required");
      return false;
    }
    if (documents.some(document => document.mandatory && !document.fileName)) {
      toast.error("Please upload all mandatory supporting documents");
      return false;
    }
    return true;
  };

  const refreshDashboard = () => {
    dispatch(getInstructionList({ blNum: blId, country: blCountry }));
    dispatch(updateDashbaordRefresh(true));
  };

  const submitRequest = async (
    requestStatus: 'Submitted' | 'Draft' = 'Submitted',
    navigateAfterSave = true
  ) => {
    setSubmitting(true);
    try {
      const result = await submitContainerDropOff({
        blId,
        requestedPartnerId: profile?.partnerId,
        country: blCountry,
        customerEmailId: profile?.email,
        remarks: '',
        userRemarks: remarks,
        status: requestStatus,
        dndInstructionId,
        releaseType: '0',
        version,
        linerId: Number(linerId),
        containerDetails: containers.map(container => ({
          containerPickupId: container.containerPickupId ?? container.blContainerId ?? null,
          cntNo: container.containerNo,
          returnDate: formatPayloadDate(returnDates[container.containerNo]),
        })),
        dndInstructionAtt: documents
          .filter(document => document.fileName)
          .map(document => ({
            dndInstructionAttId: document.dndInstructionAttId ?? null,
            docId: document.docId,
            fileName: document.fileName,
            filePath: document.filePath ?? null,
            serverFilePath: document.serverPath ?? null,
            tempFileName: document.tempFileName ?? null,
            isVerify: 'N',
          })),
        checkList: [],
        groupLevel: 0,
      });
      if (result?.responseCode && result.responseCode.toLowerCase() !== 'success') {
        throw new Error(result.responseMsg ?? "Unable to submit request");
      }
      toast.success(result?.responseMsg ?? (
        requestStatus === 'Draft'
          ? "Container Drop Off saved as draft successfully"
          : "Container Drop Off submitted successfully"
      ));
      refreshDashboard();
      if (navigateAfterSave) onBack();
      return true;
    } catch (error: any) {
      toast.error(error?.response?.data?.responseMsg ?? error?.message ?? "Unable to submit request");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveReject = async (status: 'A' | 'R') => {
    if (!canApproveReject || submitting) return;
    const approvalRemarks = linerRemarks.trim();
    const approving = status === 'A';
    const nextStatus = approving ? 'Completed' : 'Rejected';
    if (!approving && !approvalRemarks) {
       toast.dark(t('resource_message:remarksMandatory'));
      return;
    }
    setSubmitting(true);
    try {
      const result = await dispatch(approveRejectDoExtension({
        instructionId: dndInstructionId || null,
        blId,
        country: blCountry,
        instructionType: 'CONTAINER_DROP_OFF',
        linerRemarks: approvalRemarks || null,
        customerRemarks: null,
        status: status || null,
        emailTo: customerEmailId || null,
        isVerify: null,
        requestType: INSTRUCTION_TYPE,
        requestedPartnerId: profile?.partnerId,
        groupLevel: null,
        actionBy: null,
        checkList: [],
        demurrageInstLinerAttDtls: [],
        linerId: Number(linerId),
        cfsPartnerId: null,
        emptyYardPartnerId: null,
        nextLevel: null,
        unstuffing: '',
        version,
      })).unwrap();
      if (String(result?.responseCode ?? '').toLowerCase() !== nextStatus.toLowerCase()) {
        throw new Error(result?.responseMsg);
      }
      toast.success(result.responseMsg ?? (approving ? 'Approved' : t('resource_1:rejected')));
      setRequestStatus(nextStatus);
      refreshDashboard();
      onBack();
    } catch (error: any) {
      toast.error(error?.message ?? "Unable to process Container Drop Off");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!validate() || hasActiveUpload) return;
    if (!dndRequestExists) {
      setShowDndSoftStop(true);
      return;
    }
    submitRequest('Submitted');
  };

  const handleSaveDraft = () => {
    if (hasActiveUpload) return;
    submitRequest('Draft');
  };

  const resetForm = () => {
    setReturnDates({});
    setDocuments(buildConfiguredDocuments(attachmentConfig));
    setRemarks('');
    setReturnDateForAll(null);
  };

  const navigateToDnd = () => {
    localStorage.setItem('containerDropOffReturnPath', window.location.pathname);
    navigate(
      `${process.env.REACT_APP_APP_PATH_PREFIX || ''}/bl/${encodeURIComponent(encryptedBlId)}/instruction/${AESEncrypt('816')}`
    );
  };

  const handleDndSoftStopYes = async () => {
    setShowDndSoftStop(false);
    const draftSaved = await submitRequest('Draft', false);
    if (draftSaved) navigateToDnd();
  };

  const documentLink = (document: DocumentRow) =>
    document.dndInstructionAttId
      ? `${process.env.REACT_APP_CLIENT_APP_HOST}/api/edo-rest-v2/v2/common/openDocument?id=${encryptAndEncode(document.dndInstructionAttId)}&moduleTp=DND&linerId=${encryptAndEncode(linerId)}`
      : undefined;

  const containerColumns: IDataTableColumn[] = [
    {
      accessor: 'containerNo',
      Header: t('resource_2:containerNo'),
      width: 320,
      maxWidth: 320,
    },
    {
      accessor: 'returnDate',
      Header: t('resource_1:intendedReturnDate'),
      width: 450,
      maxWidth: 450,
      Cell: ({ row }: any) => {
        const containerNo = row.original.containerNo;
        return readOnly ? (
          returnDates[containerNo]
            ? moment(returnDates[containerNo]).format(getDateFormat().toUpperCase())
            : ''
        ) : (
          <DatePicker
            selected={returnDates[containerNo] ?? null}
            portalId='root-portal'
            onChange={date => setReturnDates(values => ({ ...values, [containerNo]: date }))}
            className='form-control form-control-sm'
            dateFormat={getDateFormat()}
            minDate={new Date()}
            isClearable
            popperProps={{ strategy: 'fixed' }}
          />
        );
      },
    },
  ];

  const documentColumns: IDataTableColumn[] = [
    {
      accessor: 'docName',
      Header: t('resource_2:documentName'),
      width: 180,
      maxWidth: 180,
      Cell: ({ row }: any) => {
        const documentRow: DocumentRow = row.original;
        return (
          <div className='d-flex align-items-center'>
            {documentRow.mandatory && <span className='text-danger me-1'>*</span>}
            {!readOnly && !documentRow.mandatory ? (
              <Select
                className='w-100'
                classNamePrefix='react-select'
                options={configuredDocumentOptions}
                value={configuredDocumentOptions.find(option => option.value === documentRow.docId) ?? null}
                onChange={(option: any) => updateDocument(documentRow.key, {
                  docId: option?.value ?? null,
                  docName: option?.label ?? '',
                })}
                menuPortalTarget={window.document.body}
                menuPosition='fixed'
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                  control: base => ({ ...base, minHeight: '31px', height: '31px' }),
                  valueContainer: base => ({ ...base, height: '31px', padding: '0 6px' }),
                  indicatorsContainer: base => ({ ...base, height: '31px' }),
                  menu: base => ({ ...base, fontSize: '12px' }),
                  menuList: base => ({ ...base, padding: 0 }),
                  option: base => ({ ...base, padding: '5px 8px', lineHeight: 1.2 }),
                }}
                placeholder={t('resource_1:selectDocument')}
                isClearable
              />
            ) : documentRow.docName}
          </div>
        );
      },
    },
    {
      accessor: 'fileName',
      Header: t('resource_1:upload'),
      width: 550,
      maxWidth: 550,
      Cell: ({ row }: any) => {
        const document: DocumentRow = row.original;
        return (
          <div className='d-flex align-items-center gap-3'>
            <UploadThing
              name={document.fileName}
              loading={document.loading}
              disabled={readOnly || (hasActiveUpload && document.loading === 0)}
              fulfilled={(fileName, serverPath, tempFileName) => updateDocument(document.key, { fileName, serverPath, tempFileName, loading: 100 })}
              failed={() => updateDocument(document.key, { loading: -1, fileName: '' })}
              pending={loadingValue => updateDocument(document.key, { loading: loadingValue })}
              selected={fileName => updateDocument(document.key, { fileName, loading: 0 })}
              clear={() => removeDocument(document)}
              fileLimit={10}
              fileType={['PDF']}
              link={documentLink(document)}
              labelWidth='300px'
            />
            {!readOnly && !document.mandatory && (
              <Button
                variant='link'
                size='sm'
                className='p-0 text-warning'
                aria-label={t('resource_1:delete')}
                onClick={() => removeDocument(document)}>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Card style={{ minHeight: '100%' }}>
      <Card.Header className='d-flex align-items-center border-bottom bg-white sticky-top' style={{ zIndex: 10 }}>
        <FontAwesomeIcon icon={faArrowAltCircleLeft} className='text-warning cursor-pointer fa-lg me-2' onClick={onBack} />
        <h2 className='h5 text-primary mb-0 me-4'>{t('resource_1:containerDropOff')}</h2>
        {(displayBlNumber || oblNumber) && (
          <div className='d-flex align-items-center'>
            <span className='text-muted me-2'>{t('resource_2:blNumber')}</span>
            <span className='fw-bold me-2'>{displayBlNumber || oblNumber}</span>
            <Button variant='link' className='p-0' onClick={() => navigator.clipboard.writeText(displayBlNumber || oblNumber || '')}>
              <FontAwesomeIcon icon={faCopy} />
            </Button>
          </div>
        )}
      </Card.Header>

      <Card.Body>
        {loading ? <div className='text-center py-5'>{t('resource_1:loading')}</div> : (
          <div className='container'>
            <div className='col-lg-12 mb-1'>
              <Form.Label style={{ color: '#f5822a' }}>
                <ul style={{ paddingLeft: '15px' }}>
                  {t('resource_message:demurrageInstruction').split('\n').map((notice, index) => (
                    <li key={index}>{notice.trim()}</li>
                  ))}
                </ul>
              </Form.Label>
            </div>

            <div className='row'>
              {!readOnly && (
                <div className='col-lg-6 mb-3'>
                  <Form.Label className='small'>{t('resource_1:intendedReturnDateForAllContainers')}</Form.Label>
                  <DatePicker
                    selected={returnDateForAll}
                    onChange={date => {
                      const containerDates: DateValues = {};
                      for (const container of containers) {
                        if (container.containerNo) {
                          containerDates[container.containerNo] = date;
                        }
                      }
                      setReturnDateForAll(date);
                      setReturnDates(values => ({
                        ...values,
                        ...containerDates,
                      }));
                    }}
                    className='form-control form-control-sm'
                    dateFormat={getDateFormat()}
                    minDate={new Date()}
                    isClearable
                  />
                </div>
              )}
            </div>

            <div className='mb-3'>
              <DataTable
                data={containers}
                columns={containerColumns}
                gridName='CONTAINER_DROP_OFF_CONTAINERS'
                sortable={false}
                searchable={false}
                showColumnFilter={false}
                selection={false}
                perPage={100}
                pagination={false}
                showExport={false}
                uniqueFilterKey='containerDropOffContainers'
                autoHeight={true}
                tableMinHeight={0}
              />
            </div>

            {!readOnly && (
              <div className='mb-2'>
                <span className='badge bg-secondary mb-2'>
                  {t('resource_message:docInfo')}
                </span>
                <div>
                  <Button
                    variant='falcon-primary'
                    size='sm'
                    onClick={addDocument}
                    disabled={documents.length >= 10 || !attachmentConfig.length}>
                    <FontAwesomeIcon icon={faAdd} className='me-1' />
                    {t('resource_1:documents')}
                  </Button>
                </div>
              </div>
            )}
            <div className='mb-3'>
              <DataTable
                data={documents}
                columns={documentColumns}
                gridName='CONTAINER_DROP_OFF_DOCUMENTS'
                sortable={false}
                searchable={false}
                showColumnFilter={false}
                selection={false}
                perPage={100}
                pagination={false}
                showExport={false}
                uniqueFilterKey='containerDropOffDocuments'
                autoHeight={true}
                tableMinHeight={0}
              />
              {!documents.length && <div className='small text-center text-muted py-2'>{t('resource_1:noDocumentsUploaded')}</div>}
            </div>

            <div className='row'>
              <Form.Group className='col-lg-6 mb-4'>
                <Form.Label>{t('resource_2:user_remarks')}</Form.Label>
                <Form.Control as='textarea' rows={3} value={remarks} onChange={event => setRemarks(event.target.value)} disabled={readOnly} maxLength={500} />
              </Form.Group>
              {readOnly && (
                <Form.Group className='col-lg-6 mb-4'>
                  <Form.Label>{t('resource_2:liner_remarks')}</Form.Label>
                  <Form.Control as='textarea' rows={3} value={linerRemarks} onChange={event => setLinerRemarks(event.target.value)} disabled={!canApproveReject || submitting} maxLength={500} />
                </Form.Group>
              )}
            </div>

            <div className='d-flex justify-content-center'>
              {!readOnly && <>
                <Button variant='falcon-primary' size='sm' className='me-2' onClick={handleSubmit} disabled={submitting || hasActiveUpload}>
                  <FontAwesomeIcon icon={faSave} className='me-2' />{submitting ? t('resource_1:submitting') : t('resource_1:submit')}
                </Button>
                <Button variant='falcon-danger' size='sm' onClick={resetForm}>
                  <FontAwesomeIcon icon={faRedo} className='me-2' />{t('resource_1:reset')}
                </Button>
                {!effectiveRequestId && (
                  <Button variant='falcon-primary' size='sm' className='ms-2' onClick={handleSaveDraft} disabled={submitting || hasActiveUpload}>
                    <FontAwesomeIcon icon={faFolder} className='me-2 text-warning' />{t('resource_3:saveAsDraft')}
                  </Button>
                )}
              </>}
              {canApproveReject && <>
                <Button variant='success' size='sm' className='me-2 approve fw-bold' onClick={() => handleApproveReject('A')} disabled={submitting}>
                  <FontAwesomeIcon icon={faCheck} className='me-2' />{t('resource_1:approve')}
                </Button>
                <Button variant='danger' size='sm' className='reject fw-bold' onClick={() => handleApproveReject('R')} disabled={submitting}>
                  <FontAwesomeIcon icon={faTimes} className='me-2' />{t('resource_1:reject')}
                </Button>
              </>}
              {canEdit && (
                <Button variant='falcon-primary' size='sm' onClick={() => setReadOnly(false)}>
                  <FontAwesomeIcon icon={faEdit} className='me-2' />{t('resource_1:edit')}
                </Button>
              )}
            </div>
          </div>
        )}
      </Card.Body>

      <Modal show={showDndSoftStop} onHide={() => setShowDndSoftStop(false)} centered>
        <Modal.Header className='justify-content-end py-2 border-0'>
          <CloseButton className='m-0' aria-label={t('resource_1:close')} onClick={() => setShowDndSoftStop(false)} />
        </Modal.Header>
        <Modal.Body className='text-center'>
          {t('resource_message:detentionInstructionNotSubmitted')} <b>{displayBlNumber || oblNumber || t('resource_1:thisBl')}</b>.
          <br />{t('resource_message:submitDetentionInstruction')}
        </Modal.Body>
        <Modal.Footer className='justify-content-center border-0'>
          <Button variant='falcon-default' size='sm' onClick={() => {
            setShowDndSoftStop(false);
            submitRequest('Submitted');
          }} disabled={submitting}>{t('resource_1:noContinue')}</Button>
          <Button variant='falcon-primary' size='sm' onClick={handleDndSoftStopYes} disabled={submitting}>{t('resource_1:yes')}</Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ContainerDropOff;
